import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShieldIcon,
  TablerDashboard,
  TablerBell,
  TablerMic,
  TablerListNumbers,
  TablerPlus,
  TablerDrone,
  TablerConstructionSurvey,
  TablerUsers,
  TablerKey,
  TablerFolder,
  TablerMap,
  TablerLock,
  TablerCollapseLeft,
  TablerLogOut,
} from './Icons';
import {
  LayoutGrid,
  Mountain,
  Boxes,
  HardHat,
  ShieldAlert,
  Video,
  TrendingUp,
  FileText,
  Users,
  Settings,
  Lock,
} from 'lucide-react';
import { useModule } from '../../contexts/ModuleContext';
import { isBootstrapAdmin, getItem, getRole, AUTH_KEYS } from '../../utils/authStorage';
import { isFeatureEnabled } from '../../config/features';
import './Sidebar.css';


const ALL_ROLES = ['root', 'admin', 'operator', 'viewer'];
const ADMIN_ONLY = ['root', 'admin'];

const iconMap = {
  TablerDashboard,
  TablerBell,
  TablerMic,
  TablerListNumbers,
  TablerPlus,
  TablerDrone,
  TablerConstructionSurvey,
  TablerUsers,
  TablerKey,
  LayoutGrid,
  Mountain,
  Boxes,
  HardHat,
  ShieldAlert,
  Video,
  TrendingUp,
  FileText,
  Users,
  Settings,
  TablerFolder,
  TablerMap,
  TablerLock,
};

function Sidebar({
  collapsed,
  onToggleCollapse,
  isDarkMode,
  username = 'User',
  role,
  onLogout,
  handleSignOut,
  hasRole,
  menuGroups: menuGroupsProp,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeModule, activeProject } = useModule();

  const isConstruction = location.pathname.startsWith('/construction') || activeModule === 'construction';
  const projectSelected = Boolean(activeProject?.id);

  const logout = onLogout || handleSignOut || (() => {});

  const storedRole = (role || getRole() || '').toLowerCase();
  const displayName = (username && username !== 'User' ? username : getItem(AUTH_KEYS.USERNAME)) || '—';

  const getInitials = (name) => {
    if (!name || name === 'Guest' || name === '—') return '?';
    return name.slice(0, 2).toUpperCase();
  };

  const isHiddenMode = !onToggleCollapse;

  const isItemLocked = (item) => {
    if (!isConstruction) return false;
    if (item.path === '/construction/projects') return false;
    if (
      item.path?.startsWith('/construction/user-management') ||
      item.path?.startsWith('/construction/settings')
    ) {
      return false;
    }
    return !projectSelected;
  };

  const handleNavClick = (e, path, locked) => {
    e.preventDefault();
    if (locked) return;
    if (collapsed && onToggleCollapse) {
      onToggleCollapse();
      setTimeout(() => navigate(path), 150);
    } else {
      navigate(path);
    }
  };

  const iconColor = (isActive) => {
    if (isActive) return isConstruction ? '#F59E0B' : '#0082F8';
    return isDarkMode ? '#9CA3AF' : '#6B7280';
  };

  const defaultMenuGroups = useMemo(() => [
    {
      label: t('nav.project'),
      items: [
        { label: t('nav.projects'), icon: 'TablerFolder', path: '/projects', roles: [ALL_ROLES] },
        { label: t('nav.earthwork'), icon: 'TablerLock', locked: true, roles: [ALL_ROLES] },
        { label: t('nav.stockpiles'), icon: 'TablerLock', locked: true, roles: [ALL_ROLES] },
        { label: t('nav.safetyObservations'), icon: 'TablerLock', locked: true, roles: [ALL_ROLES] },
        { label: t('nav.progress'), icon: 'TablerLock', locked: true, roles: [ALL_ROLES] },
        { label: t('nav.reports'), icon: 'TablerLock', locked: true, roles: [ALL_ROLES] },
        { label: t('nav.siteVisuals'), icon: 'TablerMap', path: '/site-visuals', roles: [ALL_ROLES] },
      ],
    },
    {
      label: t('nav.monitoring'),
      items: [
        { label: t('nav.dashboard'), icon: 'TablerDashboard', path: '/dashboard', roles: [ALL_ROLES] },
        { label: t('nav.alerts'), icon: 'TablerBell', path: '/alerts-page', roles: [ALL_ROLES] },
        { label: t('nav.voiceAlerts'), icon: 'TablerMic', path: '/voice-prompt', roles: [ALL_ROLES] },
      ],
    },
    {
      label: t('nav.devices'),
      items: [
        { label: t('nav.deviceList'), icon: 'TablerListNumbers', path: '/device-list', roles: [ALL_ROLES] },
        { label: t('nav.addDevice'), icon: 'TablerPlus', path: '/device-order', roles: [ADMIN_ONLY] },
        { label: t('nav.droneRunway'), icon: 'TablerDrone', path: '/drone-runway', roles: [ALL_ROLES] },
      ],
    },
    {
      label: t('nav.administration'),
      items: [
        { label: t('nav.userManagement'), icon: 'TablerUsers', path: '/user-management', roles: [ALL_ROLES] },
        { label: t('nav.roleManagement'), icon: 'TablerKey', path: '/role-management', roles: [ADMIN_ONLY] },
      ],
    },
  ], [t]);

  const constructionMenuGroups = useMemo(() => [
    {
      label: 'PROJECT',
      items: [
        { label: 'Projects', icon: 'LayoutGrid', path: '/construction/projects' },
        { label: 'Site Visuals', icon: 'Video', path: '/construction/visuals' },
        { label: 'Safety Observations', icon: 'ShieldAlert', path: '/construction/observations' },
        { label: 'Earthwork', icon: 'Mountain', path: '/construction/earthwork' },
        { label: 'Stockpiles', icon: 'Boxes', path: '/construction/stockpiles' },
        { label: 'MMM', icon: 'HardHat', path: '/construction/mmm' },
        { label: 'Reports', icon: 'FileText', path: '/construction/reports', feature: 'PAGE_REPORTS' },
        { label: 'Progress', icon: 'TrendingUp', path: '/construction/progress', feature: 'PAGE_PROGRESS' },
        { label: 'Survey', icon: 'TablerConstructionSurvey', path: '/construction/survey', feature: 'PAGE_SURVEY' },
        { label: 'Aerial Data', icon: 'Video', path: '/construction/aerial', feature: 'PAGE_AERIAL_DATA' },
      ].filter((item) => !item.feature || isFeatureEnabled(item.feature)),
    },
    {
      label: 'ADMINISTRATION',
      items: [
        { label: 'User Management', icon: 'Users', path: '/construction/user-management' },
        { label: 'Project Settings', icon: 'Settings', path: '/construction/settings' },
      ].filter(() => ADMIN_ONLY.includes(storedRole)),
    },
  ].filter((group) => group.items.length > 0), [storedRole]);

  const bootstrapMenuGroups = useMemo(() => [
    {
      label: 'ADMINISTRATION',
      items: [
        { label: 'Tenant Management', icon: 'Settings', path: '/tenant-management', roles: [ALL_ROLES] },
        { label: 'User Management', icon: 'TablerUsers', path: '/user-management', roles: [ALL_ROLES] },
      ],
    },
  ], []);

  const activeGroups = menuGroupsProp || (
    isBootstrapAdmin()
      ? bootstrapMenuGroups
      : (isConstruction ? constructionMenuGroups : defaultMenuGroups)
  );


  const filteredGroups = hasRole
    ? activeGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => !item.roles || item.roles.some((r) => hasRole(r))
          ),
        }))
        .filter((g) => g.items.length > 0)
    : activeGroups;

  const sidebarClass = [
    'sidebar',
    collapsed ? 'sidebar-collapsed' : '',
    collapsed && isHiddenMode ? 'sidebar-hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={sidebarClass}>
      <div className="sidebar-inner">
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {isConstruction ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt={t('brand.company')} width="28" height="28" style={{ display: 'block', flexShrink: 0 }} />
                {!collapsed && (
                  <span className="sidebar-title" style={{ fontSize: '12px', lineHeight: '1.2', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('brand.product')} <br /><span style={{ fontWeight: '400', fontSize: '10px', color: '#9CA3AF' }}>{t('nav.construction')}</span>
                  </span>
                )}
              </div>
            ) : (
              <>
                <ShieldIcon color={isDarkMode ? '#0082F8' : '#1868B2'} size={22} />
                {!collapsed && (
                  <span className="sidebar-title" style={{ lineHeight: '1.2' }}>
                    {t('brand.product')} <br /><span style={{ fontWeight: '400', fontSize: '10px', color: '#9CA3AF' }}>{t('nav.surveillance')}</span>
                  </span>
                )}
              </>
            )}

            {!collapsed && onToggleCollapse && (
              <button className="icon-btn sidebar-collapse-btn" onClick={onToggleCollapse} style={{ marginLeft: 'auto' }}>
                <TablerCollapseLeft color={isDarkMode ? '#9CA3AF' : '#6B7280'} size={18} />
              </button>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          {filteredGroups.map((group) => (
            <div key={group.label} className="nav-group">
              <div className="nav-section-label">{group.label}</div>
              {group.items.map((item) => {
                const IconComp = iconMap[item.icon];
                const isActive = location.pathname === item.path || (item.path !== '/construction/projects' && location.pathname.startsWith(item.path));
                const locked = isItemLocked(item);
                return (
                  <a
                    key={item.label}
                    href={item.path}
                    className={`nav-item ${isActive ? 'active' : ''} ${locked ? 'locked' : ''}`}
                    onClick={(e) => handleNavClick(e, item.path, locked)}
                    aria-disabled={locked}
                    title={locked ? 'Select a project to unlock' : undefined}
                    style={
                      locked
                        ? { opacity: 0.45, cursor: 'not-allowed' }
                        : isActive && isConstruction
                          ? { borderLeftColor: '#F59E0B', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }
                          : {}
                    }
                  >
                    <span className="nav-icon">
                      {IconComp ? (
                        <IconComp color={iconColor(isActive && !locked)} size={18} />
                      ) : null}
                    </span>
                    <span className="nav-label">{item.label}</span>
                    {locked && !collapsed && (
                      <Lock size={12} color={isDarkMode ? '#6B7280' : '#9CA3AF'} style={{ marginLeft: 'auto' }} />
                    )}
                  </a>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        <div className="sidebar-divider" />
        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar" style={isConstruction ? { backgroundColor: '#1E293B', color: '#F59E0B', border: '1px solid #F59E0B' } : {}}>
              {getInitials(displayName)}
            </div>
            {!collapsed && (
              <div className="user-info">
                <span className="user-name">{displayName}</span>
                <span className="user-role">{storedRole || '—'}</span>
              </div>
            )}
            {!collapsed && (
              <button className="icon-btn logout-btn" onClick={logout} title={t("button.logout")}>
                <TablerLogOut color={isDarkMode ? '#9CA3AF' : '#6B7280'} size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
